import { OnboardingStepStatus } from "@src/components/atoms/new-onboarding-stepper/new-onboarding-stepper";
import {
  GatewayOperationError,
  GatewayOnboardingStatus,
  GatewayDetails,
} from "@src/types/gateway-onboarding";
import { Route } from "@src/routes/routes";
import { queryGatewayOperationStatusUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-gateway-operation-status-use-case";
import {
  resolveOperationFailureReason,
  resolveOperationStatus,
} from "@src/cloud-connect/utils/resolve-operation-status";
import {
  OPERATION_POLL_MAX_ATTEMPTS,
  OPERATION_POLL_MAX_DURATION_MS,
  resolveOperationErrorDetails,
  waitForNextOperationPoll,
} from "@src/cloud-connect/utils/onboarding-operation";
import { queryGatewayByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-gateway-by-id-use-case";

export type GatewayPollResult = {
  status: "success" | "error" | "pending";
  reason?: string;
  details?: GatewayOperationError;
};

export const getGatewayDrawerTexts = (i18n: (key: string) => string) => ({
  title: i18n("modules.addNewGatewayDrawerModal.title"),
  gatewayOnboardingSection: i18n(
    "modules.addNewGatewayDrawerModal.gatewayOnboardingSection",
  ),
  onboardingKey: i18n("modules.addNewGatewayDrawerModal.onboardingKey"),
  help: i18n("modules.addNewGatewayDrawerModal.help"),
  onboardingKeyHelpText: i18n(
    "modules.addNewGatewayDrawerModal.onboardingKeyHelpText",
  ),
  connectGatewayButton: i18n(
    "modules.addNewGatewayDrawerModal.connectGatewayButton",
  ),
  finishAndGoToGateway: i18n(
    "modules.addNewGatewayDrawerModal.finishAndGoToGateway",
  ),
  continueMachineOnboarding: i18n(
    "modules.addNewGatewayDrawerModal.continueMachineOnboarding",
  ),
  invalidOnboardingKeyErrorMessage: i18n(
    "modules.addNewGatewayDrawerModal.invalidOnboardingKeyErrorMessage",
  ),
  announceFailed: i18n("modules.addNewGatewayDrawerModal.announceFailed"),
  gatewayConnectionNotEstablished: i18n(
    "modules.addNewGatewayDrawerModal.gatewayConnectionNotEstablished",
  ),
  continueMachineOnboardingDisabled: i18n(
    "modules.addNewGatewayDrawerModal.continueMachineOnboardingDisabled",
  ),
  stepGateway: i18n("modules.addNewMachineDrawerModal.stepGateway"),
  stepMachine: i18n("modules.addNewMachineDrawerModal.stepMachine"),
  stepThingsOs: i18n("modules.addNewMachineDrawerModal.stepThingsOs"),
});

export const getGatewayOnboardingSteps = (
  isGatewayOnboardingCompleted: boolean,
  texts: {
    stepGateway: string;
    stepMachine: string;
    stepThingsOs: string;
  },
): { label: string; status: OnboardingStepStatus }[] => {
  if (isGatewayOnboardingCompleted) {
    return [
      { label: texts.stepGateway, status: "completed" },
      { label: texts.stepMachine, status: "active" },
      { label: texts.stepThingsOs, status: "pending" },
    ];
  }

  return [
    { label: texts.stepGateway, status: "active" },
    { label: texts.stepMachine, status: "pending" },
    { label: texts.stepThingsOs, status: "pending" },
  ];
};

export const pollOnboardingOperation = async (
  operationId: string,
  signal?: AbortSignal,
): Promise<GatewayPollResult> => {
  for (let attempt = 0; attempt < OPERATION_POLL_MAX_ATTEMPTS; attempt++) {
    const result = await queryGatewayOperationStatusUseCase(
      operationId,
      signal,
    );

    if (!result.success) {
      const details = resolveOperationErrorDetails(undefined, result.errorData);
      return {
        status: "error",
        reason: details?.title || result.error,
        details,
      };
    }

    const operation = result.data;
    const status = resolveOperationStatus(operation);
    if (status === "success") {
      return { status };
    }

    if (status === "error") {
      return {
        status,
        reason: resolveOperationFailureReason(operation),
        details: resolveOperationErrorDetails(operation),
      };
    }

    if (attempt < OPERATION_POLL_MAX_ATTEMPTS - 1) {
      await waitForNextOperationPoll(signal);
    }
  }

  return { status: "pending" };
};

export const pollGatewayConnection = async (
  gatewayId: string,
  signal?: AbortSignal,
): Promise<GatewayDetails | undefined> => {
  let latestGateway: GatewayDetails | undefined;
  const deadline = performance.now() + OPERATION_POLL_MAX_DURATION_MS;

  for (let attempt = 0; performance.now() < deadline; attempt++) {
    const remainingMs = deadline - performance.now();
    if (remainingMs <= 0 || signal?.aborted) {
      return latestGateway;
    }

    const requestController = new AbortController();
    const abortRequest = () => requestController.abort();
    signal?.addEventListener("abort", abortRequest, { once: true });
    let resolveRequestTimeout: (value: undefined) => void = () => undefined;
    const requestTimeout = new Promise<undefined>((resolve) => {
      resolveRequestTimeout = resolve;
    });
    const timeoutId = setTimeout(() => {
      requestController.abort();
      resolveRequestTimeout(undefined);
    }, remainingMs);
    const result = await Promise.race([
      queryGatewayByIdUseCase(gatewayId, requestController.signal).catch(
        () => undefined,
      ),
      requestTimeout,
    ]);
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", abortRequest);

    if (result?.success && result.data) {
      latestGateway = result.data;
      if (
        result.data.connection_counter &&
        result.data.connection_counter > 0
      ) {
        return result.data;
      }
    }

    if (performance.now() < deadline) {
      const waitRemainingMs = deadline - performance.now();
      if (waitRemainingMs <= 0) {
        return latestGateway;
      }

      try {
        await Promise.race([
          waitForNextOperationPoll(signal),
          new Promise<void>((resolve) => setTimeout(resolve, waitRemainingMs)),
        ]);
      } catch {
        return latestGateway;
      }
    }
  }

  return latestGateway;
};

export const isGatewayOnboardingCompletedStatus = (
  gatewayOnboardingStatus: GatewayOnboardingStatus,
): boolean =>
  gatewayOnboardingStatus ===
  GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED;

export const getGatewayTargetRoute = (gatewayId?: string): string => {
  const route = Route.GATEWAYS;
  const trimmedGatewayId = gatewayId?.trim();

  if (!trimmedGatewayId) {
    return route;
  }

  return `${route}?gateway_uuid=${encodeURIComponent(trimmedGatewayId)}`;
};

export const resetGatewayLocalState = (params: {
  setOnboardingKey?: (value: string) => void;
  setIsConnectGatewayLoading?: (value: boolean) => void;
  setOperationFailureReason: (value: string) => void;
  setOperationFailureDetails: (value: GatewayOperationError | null) => void;
  setShowProtocolDetails: (value: boolean) => void;
  setIsGatewayAnnounced: (value: boolean) => void;
}) => {
  params.setOnboardingKey?.("");
  params.setIsConnectGatewayLoading?.(false);
  params.setOperationFailureReason("");
  params.setOperationFailureDetails(null);
  params.setShowProtocolDetails(false);
  params.setIsGatewayAnnounced(false);
};
